import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { Router, RouterLink, RouterOutlet } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import {
  NgxOlControlFullScreenComponent,
  NgxOlControlScaleLineComponent,
  NgxOlControlZoomSliderComponent,
  NgxOlLayerTileComponent,
  NgxOlLayerVectorTileComponent,
  NgxOlMapComponent,
  NgxOlOverlayComponent,
  NgxOlSourceOSMComponent,
  NgxOlSourceVectorTileComponent,
  NgxOlViewComponent,
} from '@sungissia/ol-ngx';

import { Map, MapBrowserEvent } from 'ol';
import { platformModifierKeyOnly } from 'ol/events/condition';
import { FeatureLike } from 'ol/Feature';
import MVT from 'ol/format/MVT.js';
import { Image } from 'ol/layer';
import VectorTileLayer from 'ol/layer/VectorTile';
import { Vector, VectorTile } from 'ol/source';
import { Fill, Stroke, Style } from 'ol/style';
import { FlatStyleLike } from 'ol/style/flat';
import IDW from 'ol-ext/source/IDW';

import { JobStore } from '@modules/job/jobs.store';
import { JobItemComponent } from '@modules/job/list/job-list-item.component';
import { SeedingStore } from '@modules/seeding/seeding.store';
import { PermissionDirective } from '@shared/directives/permission.directive';

import { TerritoryTypesStore } from '../../territory-types.store';
import { HeaderComponent } from '../header/header.component';
import { ColorSwitcherComponent } from './color-switcher/color-switcher.component';
import { MapStore } from './map.store';
import { SelectorControlComponent } from './selector-control/selector-control.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    RouterLink,
    HeaderComponent,
    NgxOlMapComponent,
    NgxOlSourceOSMComponent,
    NgxOlSourceVectorTileComponent,
    NgxOlViewComponent,
    NgxOlLayerTileComponent,
    NgxOlLayerVectorTileComponent,
    NgxOlControlFullScreenComponent,
    NgxOlControlScaleLineComponent,
    NgxOlControlZoomSliderComponent,
    NgxOlOverlayComponent,
    ColorSwitcherComponent,
    TranslatePipe,
    JobItemComponent,
    SelectorControlComponent,
    PermissionDirective,
  ],
  selector: 'norupe-map-layout',
  templateUrl: './map-layout.component.html',
})
export class MapLayoutComponent {
  @ViewChild('map', { static: true })
  map!: NgxOlMapComponent;

  @ViewChild('territoriesLayer')
  territoriesLayer!: NgxOlLayerVectorTileComponent;

  readonly territoryTypesStore = inject(TerritoryTypesStore);
  readonly jobStore = inject(JobStore);
  readonly mapStore = inject(MapStore);
  readonly seedingStore = inject(SeedingStore);
  readonly router = inject(Router);

  entrypoint = window.location.origin.includes('localhost')
    ? 'http://localhost:7800/'
    : `${window.location.origin}/tiles/`;

  territoiesAndSlotsURL =
    this.entrypoint + 'public.territories_territory/{z}/{x}/{y}.pbf';

  territoiesURL = this.entrypoint + 'public.territory_mvt/{z}/{x}/{y}.pbf';
  territoriesSlotURL =
    this.entrypoint + 'public.territory_slot_mvt/{z}/{x}/{y}.pbf';

  overlayPosition = signal([0, 0]);

  featureOnCursor = signal<FeatureLike[]>([]);

  idwLayer = new IDW({
    source: new Vector(),
  });

  imageLayer = new Image({});

  selectedTerritoriesLayer = new VectorTileLayer({});

  selectionLayerStyle = new Style({
    fill: new Fill({ color: 'rgba(246, 227, 66, 0.3)' }),
    stroke: new Stroke({ color: 'yellow', width: 5 }),
  });

  MVT = new MVT();

  territoryStyle = computed(() => {
    const response = this.territoryTypesStore.entities();
    const flatStyleLike: FlatStyleLike = [];
    if (response !== null) {
      response.forEach((element) => {
        const flatStyle = {
          filter: ['==', ['get', 'type_id'], element.id],
          style: element.style_json,
        };
        flatStyleLike.push(flatStyle);
      });
    }

    const flatsStyle = {
      filter: [
        'in',
        ['get', 'id'],
        ['literal', this.jobStore.uniqueTerritoryID().flat()],
      ],
      style: {
        'fill-color': 'rgba(246, 227, 66, 0.2)',
        'stroke-color': 'yellow',
        'stroke-width': 1.5,
      },
    };
    flatStyleLike.push(flatsStyle);

    return flatStyleLike;
  });

  territorySlotsStyle = computed(() => {
    const flatStyleLike: FlatStyleLike = [];

    flatStyleLike.push(
      {
        filter: ['==', ['get', 'is_empty'], true],
        style: {
          'fill-color': 'rgb(170, 170, 170)',
          'stroke-color': 'black',
          'stroke-width': 1,
        },
      },
      {
        filter: ['==', ['get', 'is_empty'], false],
        style: {
          'fill-color': 'rgba(0,255,0,1)',
          'stroke-color': 'black',
          'stroke-width': 1,
          'text-align': 'center',
          'text-stroke-width': 1,
          'text-value': [
            'case',
            ['has', 'seeding_id'],
            ['to-string', ['get', 'seeding_id']],
            '',
          ],
        },
      },
    );

    return flatStyleLike;
  });

  constructor() {
    effect(() => {
      this.mapStore.selectedTerritoriesIds();
      this.selectedTerritoriesLayer.changed();
    });
    effect(() => {
      this.seedingStore.selectedSeedingSlotsIds();
      this.selectedTerritoriesLayer.changed();
    });
    effect(() => {
      this.seedingStore.changesCounter();
      if (this.territoriesLayer) {
        this.territoriesLayer.getInstance().getSource()?.refresh();
      }
    });
  }

  selectedTerritoriesVectorTileLayer(map: Map) {
    map.once('loadend', () => {
      this.selectedTerritoriesLayer = new VectorTileLayer({
        map,
        renderMode: 'vector',
        source: this.territoriesLayer.getInstance().getSource() as VectorTile,
        style: (feature) =>
          this.mapStore
            .selectedTerritoriesIds()
            .has(feature.get('id') as string) ||
          this.seedingStore
            .selectedSeedingSlotsIds()
            .has(feature.get('id') as string)
            ? this.selectionLayerStyle
            : undefined,
      });
    });
  }

  jobListByTerritor(territoryID: string) {
    return this.jobStore
      .entities()
      .filter((job) =>
        job.territories.find((territory) => territory === territoryID),
      );
  }

  updateStoreZoom() {
    this.mapStore.setZoom(this.map.getInstance().getView().getZoom());
  }

  closePopup() {
    this.featureOnCursor.set([]);
    this.overlayPosition.set([0, 0]);
  }

  selectedObject(event: MapBrowserEvent) {
    //no popup when ctrl is held
    if (platformModifierKeyOnly(event)) {
      return;
    }

    if (this.featureOnCursor() === event.map.getFeaturesAtPixel(event.pixel)) {
      this.overlayPosition.set([0, 0]);
      return;
    }

    this.featureOnCursor.set(
      event.map
        .getFeaturesAtPixel(event.pixel)
        .filter(
          (feature, index, arr) =>
            arr.findIndex((f) => f.get('id') === feature.get('id')) === index,
        ),
    );
    if (this.featureOnCursor()[0]) {
      this.overlayPosition.set(event.coordinate);
    } else {
      this.overlayPosition.set([0, 0]);
    }
  }
}
